import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MessageSquare, ArrowLeft, Copy, Check, Code, Download, FileText, Puzzle, Tag, ShoppingBag, Layout, Globe, Send, X, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EmbedPage = () => {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState({});
  const [showSendModal, setShowSendModal] = useState(false);
  const [sending, setSending] = useState(false);
  const [developerForm, setDeveloperForm] = useState({
    developerEmail: '',
    developerName: '',
    platform: 'general',
    senderName: '',
    senderFirm: ''
  });
  
  // Include firm_id so the widget loads this firm's branding
  const firmId = user?.id || '';
  const embedCode = `<!-- ${user?.firm_name || 'Chat'} Widget -->
<script>
  (function() {
    var script = document.createElement('script');
    script.src = '${process.env.REACT_APP_BACKEND_URL}/widget.js?firm_id=${firmId}';
    script.async = true;
    document.body.appendChild(script);
  })();
</script>`;

  const handleCopy = () => {
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    toast.success('Code copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = async (platform, endpoint, filename) => {
    setDownloading(prev => ({ ...prev, [platform]: true }));
    try {
      const response = await fetch(`${API_URL}/api/widget/download/${endpoint}`);
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`${platform} file downloaded!`);
    } catch (error) {
      toast.error('Failed to download file');
    } finally {
      setDownloading(prev => ({ ...prev, [platform]: false }));
    }
  };

  const handleSendToDeveloper = async (e) => {
    e.preventDefault();
    if (!developerForm.developerEmail) {
      toast.error('Please enter developer email');
      return;
    }
    
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('developer_email', developerForm.developerEmail);
      formData.append('developer_name', developerForm.developerName);
      formData.append('platform', developerForm.platform);
      formData.append('sender_name', developerForm.senderName);
      formData.append('sender_firm', developerForm.senderFirm || user?.firm_name || '');
      formData.append('firm_id', firmId);
      
      const response = await fetch(`${API_URL}/api/widget/send-to-developer`, {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send');
      }
      
      toast.success(`Instructions sent to ${developerForm.developerEmail}!`);
      setShowSendModal(false);
      setDeveloperForm({
        developerEmail: '',
        developerName: '',
        platform: 'general',
        senderName: '',
        senderFirm: ''
      });
    } catch (error) {
      toast.error(error.message || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const platforms = [
    {
      id: 'wordpress',
      name: 'WordPress',
      description: 'One-click plugin install. Just upload and activate.',
      icon: Puzzle,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      buttonText: 'Download Plugin',
      buttonStyle: 'bg-blue-600 hover:bg-blue-700 text-white',
      endpoint: 'wordpress-plugin',
      filename: 'intakepal-widget-wordpress.zip',
      fileType: '.zip'
    },
    {
      id: 'gtm',
      name: 'Google Tag Manager',
      description: 'Import container file. Works on any website with GTM.',
      icon: Tag,
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
      buttonText: 'Download Container',
      buttonStyle: 'bg-amber-500 hover:bg-amber-600 text-white',
      endpoint: 'google-tag-manager',
      filename: 'intakepal-gtm-container.json',
      fileType: '.json'
    },
    {
      id: 'shopify',
      name: 'Shopify',
      description: 'Liquid snippet file. Add to your theme snippets.',
      icon: ShoppingBag,
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
      buttonText: 'Download Snippet',
      buttonStyle: 'bg-green-600 hover:bg-green-700 text-white',
      endpoint: 'shopify',
      filename: 'intakepal-widget.liquid',
      fileType: '.liquid'
    },
    {
      id: 'wix',
      name: 'Wix',
      description: 'Step-by-step guide with code ready to paste.',
      icon: Layout,
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600',
      buttonText: 'Download Guide',
      buttonStyle: 'bg-purple-600 hover:bg-purple-700 text-white',
      endpoint: 'wix',
      filename: 'intakepal-wix-install.txt',
      fileType: '.txt'
    },
    {
      id: 'squarespace',
      name: 'Squarespace',
      description: 'Simple instructions for Code Injection settings.',
      icon: Globe,
      iconBg: 'bg-slate-800',
      iconColor: 'text-white',
      buttonText: 'Download Guide',
      buttonStyle: 'bg-slate-800 hover:bg-slate-900 text-white',
      endpoint: 'squarespace',
      filename: 'intakepal-squarespace-install.txt',
      fileType: '.txt'
    },
    {
      id: 'other',
      name: 'Other Platforms',
      description: 'Universal guide for Webflow, HTML sites & more.',
      icon: FileText,
      iconBg: 'bg-teal-100',
      iconColor: 'text-teal-600',
      buttonText: 'Download Guide',
      buttonStyle: 'bg-teal-600 hover:bg-teal-700 text-white',
      endpoint: 'instructions',
      filename: 'intakepal-widget-instructions.txt',
      fileType: '.txt'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" className="text-slate-600" data-testid="back-dashboard-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                IntakePal
              </span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-12">
        <div className="mb-8">
          <h1 
            className="text-3xl font-bold text-slate-900 mb-2"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Install Chat Widget
          </h1>
          <p className="text-slate-600">
            Choose your website platform and download the ready-to-use installation file.
          </p>
        </div>

        {/* Platform Download Grid */}
        <Card className="border-slate-200 mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Download className="w-5 h-5 text-teal-600" />
              Choose Your Platform
            </CardTitle>
            <CardDescription>
              Download the installation file for your website platform - no coding required
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {platforms.map((platform) => {
                const IconComponent = platform.icon;
                return (
                  <div 
                    key={platform.id}
                    className="bg-white rounded-xl p-5 border border-slate-200 hover:border-slate-300 hover:shadow-md transition-all"
                  >
                    <div className="flex items-start gap-3 mb-4">
                      <div className={`w-10 h-10 ${platform.iconBg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                        <IconComponent className={`w-5 h-5 ${platform.iconColor}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-slate-900">{platform.name}</h3>
                        <p className="text-xs text-slate-500 mt-0.5">{platform.fileType} file</p>
                      </div>
                    </div>
                    <p className="text-sm text-slate-600 mb-4 min-h-[40px]">
                      {platform.description}
                    </p>
                    <Button 
                      onClick={() => handleDownload(platform.name, platform.endpoint, platform.filename)}
                      disabled={downloading[platform.name]}
                      className={`w-full ${platform.buttonStyle}`}
                      data-testid={`download-${platform.id}-btn`}
                    >
                      {downloading[platform.name] ? (
                        <>Downloading...</>
                      ) : (
                        <>
                          <Download className="w-4 h-4 mr-2" />
                          {platform.buttonText}
                        </>
                      )}
                    </Button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Send to Developer Section */}
        <Card className="border-slate-200 mb-6 bg-gradient-to-r from-indigo-50 to-purple-50">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Mail className="w-6 h-6 text-indigo-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Have a Web Developer?
                </h3>
                <p className="text-sm text-slate-600">
                  We&apos;ll email them the installation instructions with everything they need.
                </p>
              </div>
              <Button 
                onClick={() => setShowSendModal(true)}
                className="bg-indigo-600 hover:bg-indigo-700 text-white w-full sm:w-auto"
                data-testid="send-to-developer-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                Send to My Developer
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Manual Code Section */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Code className="w-5 h-5 text-slate-500" />
              Manual Installation
            </CardTitle>
            <CardDescription>
              For developers: copy and paste this code before the closing &lt;/body&gt; tag
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="relative">
              <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto text-sm">
                <code>{embedCode}</code>
              </pre>
              <Button
                size="sm"
                variant="secondary"
                className="absolute top-3 right-3"
                onClick={handleCopy}
                data-testid="copy-embed-btn"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 mr-1" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-1" />
                    Copy
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="mt-8 p-6 bg-teal-50 rounded-xl border border-teal-200">
          <h3 className="font-semibold text-teal-900 mb-2">Need help?</h3>
          <p className="text-teal-700 text-sm">
            If you need assistance installing the widget on your website, our support team is here to help.
            Contact us at support@intakepal.com
          </p>
        </div>
      </main>

      {/* Send to Developer Modal */}
      {showSendModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="bg-indigo-600 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <Send className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-lg font-semibold text-white">Send to Developer</h2>
              </div>
              <button 
                onClick={() => setShowSendModal(false)}
                className="text-white/80 hover:text-white"
                data-testid="close-send-modal-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSendToDeveloper} className="p-6 space-y-4">
              <div>
                <Label htmlFor="developerEmail" className="text-slate-700">
                  Developer&apos;s Email <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="developerEmail"
                  type="email"
                  placeholder="developer@example.com"
                  value={developerForm.developerEmail}
                  onChange={(e) => setDeveloperForm(prev => ({ ...prev, developerEmail: e.target.value }))}
                  required
                  className="mt-1"
                  data-testid="developer-email-input"
                />
              </div>
              
              <div>
                <Label htmlFor="developerName" className="text-slate-700">
                  Developer&apos;s Name (optional)
                </Label>
                <Input
                  id="developerName"
                  type="text"
                  placeholder="John"
                  value={developerForm.developerName}
                  onChange={(e) => setDeveloperForm(prev => ({ ...prev, developerName: e.target.value }))}
                  className="mt-1"
                  data-testid="developer-name-input"
                />
              </div>
              
              <div>
                <Label htmlFor="platform" className="text-slate-700">
                  Website Platform
                </Label>
                <Select
                  value={developerForm.platform}
                  onValueChange={(value) => setDeveloperForm(prev => ({ ...prev, platform: value }))}
                >
                  <SelectTrigger className="mt-1" data-testid="platform-select">
                    <SelectValue placeholder="Select platform" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">Not sure / Other</SelectItem>
                    <SelectItem value="wordpress">WordPress</SelectItem>
                    <SelectItem value="gtm">Google Tag Manager</SelectItem>
                    <SelectItem value="shopify">Shopify</SelectItem>
                    <SelectItem value="wix">Wix</SelectItem>
                    <SelectItem value="squarespace">Squarespace</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="border-t pt-4 mt-4">
                <p className="text-xs text-slate-500 mb-3">Your info (shown in email)</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="senderName" className="text-slate-700 text-sm">Your Name</Label>
                    <Input
                      id="senderName"
                      type="text"
                      placeholder="Your name"
                      value={developerForm.senderName}
                      onChange={(e) => setDeveloperForm(prev => ({ ...prev, senderName: e.target.value }))}
                      className="mt-1"
                      data-testid="sender-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="senderFirm" className="text-slate-700 text-sm">Firm Name</Label>
                    <Input
                      id="senderFirm"
                      type="text"
                      placeholder="Law firm name"
                      value={developerForm.senderFirm}
                      onChange={(e) => setDeveloperForm(prev => ({ ...prev, senderFirm: e.target.value }))}
                      className="mt-1"
                      data-testid="sender-firm-input"
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowSendModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={sending}
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                  data-testid="send-email-btn"
                >
                  {sending ? (
                    <>Sending...</>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Instructions
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmbedPage;
